import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { Conversation, Step } from './scenarios';
import { seededConversations } from './scenarios';

export type Speed = 1 | 2 | 'instant';
export type StepPhase = 'pending' | 'running' | 'done';

export interface RunStep {
  step: Step;
  phase: StepPhase;
  /** Only meaningful for message steps: how many word-tokens are visible. */
  wordsShown: number;
  /** Resolved once at PLAY time so replays/tests are deterministic. */
  durationMs: number;
  /** "HH:MM" — from the script for seeded chats, stamped at completion for live ones. */
  time?: string;
}

export interface ConvState {
  key: string; // unique per run (a conversation can be replayed)
  conversation: Conversation;
  steps: RunStep[];
  status: 'playing' | 'done';
}

export interface PlayerState {
  convs: ConvState[];
  /** null → "Нов чат" empty state */
  activeKey: string | null;
  speed: Speed;
}

export type PlayerAction =
  | { type: 'PLAY'; conversation: Conversation; key: string; durations: number[] }
  | { type: 'SELECT'; key: string }
  | { type: 'NEW_CHAT' }
  | { type: 'STEP_DONE'; now: string }
  | { type: 'WORD_TICK'; count: number; now: string }
  | { type: 'SET_SPEED'; speed: Speed; now: string }
  | { type: 'SKIP'; now: string }
  | { type: 'CLEAR' };

const DEFAULT_WPM = 250;
/** Words are streamed in small batches to keep re-renders cheap. */
const WORDS_PER_TICK = 2;

/** Tokenize preserving whitespace (incl. newlines) so markdown survives streaming. */
export function tokenizeWords(content: string): string[] {
  return content.match(/\S+\s*/g) ?? [];
}

export function wordCount(content: string): number {
  return tokenizeWords(content).length;
}

export function formatNow(date: Date = new Date()): string {
  return date.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' });
}

export function resolveDuration(step: Step, index: number, random: () => number): number {
  if (step.kind === 'message') return 0; // paced by word ticks, not duration
  if (step.durationMs !== undefined) return step.durationMs;
  if (step.kind === 'user') return index === 0 ? 200 : 1400; // pause before a follow-up appears
  if (step.kind === 'thinking') return Math.round(500 + random() * 400); // ~700ms
  return Math.round(800 + random() * 1000); // tool: 800–1800ms
}

function stampTime(step: RunStep, now: string): RunStep {
  if (step.time || (step.step.kind !== 'user' && step.step.kind !== 'message')) return step;
  const scripted = step.step.time;
  return { ...step, time: scripted ?? now };
}

function finishConv(conv: ConvState, now: string): ConvState {
  return {
    ...conv,
    status: 'done',
    steps: conv.steps.map((s) =>
      stampTime(
        {
          ...s,
          phase: 'done',
          wordsShown: s.step.kind === 'message' ? wordCount(s.step.content) : s.wordsShown,
        },
        now
      )
    ),
  };
}

/** Replace only the playing conversation so finished ones stay referentially stable. */
function withPlaying(state: PlayerState, update: (conv: ConvState) => ConvState): PlayerState {
  const idx = state.convs.findIndex((c) => c.status === 'playing');
  if (idx === -1) return state;
  const convs = state.convs.slice();
  convs[idx] = update(convs[idx]);
  return { ...state, convs };
}

function advance(conv: ConvState, now: string): ConvState {
  const idx = conv.steps.findIndex((s) => s.phase !== 'done');
  if (idx === -1) return { ...conv, status: 'done' };
  const steps = conv.steps.slice();
  steps[idx] = stampTime(
    {
      ...steps[idx],
      phase: 'done',
      wordsShown:
        steps[idx].step.kind === 'message' ? wordCount(steps[idx].step.content) : steps[idx].wordsShown,
    },
    now
  );
  const next = idx + 1;
  if (next < steps.length) {
    steps[next] = { ...steps[next], phase: 'running' };
    return { ...conv, steps };
  }
  return { ...conv, steps, status: 'done' };
}

function seededConvState(conversation: Conversation): ConvState {
  return {
    key: `seeded-${conversation.id}`,
    conversation,
    status: 'done',
    steps: conversation.steps.map((step) => ({
      step,
      phase: 'done',
      wordsShown: step.kind === 'message' ? wordCount(step.content) : 0,
      durationMs: 0,
      time: step.kind === 'user' || step.kind === 'message' ? step.time : undefined,
    })),
  };
}

export function createInitialState(): PlayerState {
  return { convs: seededConversations.map(seededConvState), activeKey: null, speed: 1 };
}

export function playerReducer(state: PlayerState, action: PlayerAction): PlayerState {
  switch (action.type) {
    case 'PLAY': {
      // Fast-forward any run still playing before starting the next one.
      const base = withPlaying(state, (c) => finishConv(c, formatNow()));
      const steps: RunStep[] = action.conversation.steps.map((step, i) => ({
        step,
        phase: i === 0 ? 'running' : 'pending',
        wordsShown: 0,
        durationMs: action.durations[i] ?? 0,
      }));
      let conv: ConvState = {
        key: action.key,
        conversation: action.conversation,
        steps,
        status: 'playing',
      };
      if (state.speed === 'instant') conv = finishConv(conv, formatNow());
      return { ...base, convs: [conv, ...base.convs], activeKey: action.key };
    }
    case 'SELECT':
      return { ...state, activeKey: action.key };
    case 'NEW_CHAT':
      return { ...state, activeKey: null };
    case 'STEP_DONE':
      return withPlaying(state, (c) => advance(c, action.now));
    case 'WORD_TICK':
      return withPlaying(state, (conv) => {
        const idx = conv.steps.findIndex((s) => s.phase === 'running');
        if (idx === -1 || conv.steps[idx].step.kind !== 'message') return conv;
        const total = wordCount((conv.steps[idx].step as { content: string }).content);
        const shown = Math.min(conv.steps[idx].wordsShown + action.count, total);
        if (shown >= total) return advance(conv, action.now);
        const steps = conv.steps.slice();
        steps[idx] = { ...steps[idx], wordsShown: shown };
        return { ...conv, steps };
      });
    case 'SET_SPEED': {
      const next = { ...state, speed: action.speed };
      return action.speed === 'instant' ? withPlaying(next, (c) => finishConv(c, action.now)) : next;
    }
    case 'SKIP':
      return withPlaying(state, (c) => finishConv(c, action.now));
    case 'CLEAR':
      return { ...createInitialState(), speed: state.speed };
    default:
      return state;
  }
}

export interface ScenarioPlayer {
  convs: ConvState[];
  activeConv: ConvState | null;
  activeKey: string | null;
  speed: Speed;
  isPlaying: boolean;
  play: (conversation: Conversation) => void;
  select: (key: string) => void;
  newChat: () => void;
  skip: () => void;
  clear: () => void;
  setSpeed: (speed: Speed) => void;
}

export function useScenarioPlayer(options?: { random?: () => number }): ScenarioPlayer {
  const randomRef = useRef(options?.random ?? Math.random);
  const [state, dispatch] = useReducer(playerReducer, undefined, createInitialState);

  const playing = state.convs.find((c) => c.status === 'playing');
  const activeConv = state.convs.find((c) => c.key === state.activeKey) ?? null;

  // Single self-rescheduling timer: every dispatch re-runs this effect and its
  // cleanup clears the previous timeout, so at most one timer is ever pending —
  // skip/instant can never race a stale timeout.
  useEffect(() => {
    const conv = state.convs.find((c) => c.status === 'playing');
    if (!conv || state.speed === 'instant') return;
    const idx = conv.steps.findIndex((s) => s.phase === 'running');
    if (idx === -1) return;
    const current = conv.steps[idx];
    const factor = state.speed;

    let timer: ReturnType<typeof setTimeout>;
    if (current.step.kind === 'message') {
      const wpm = current.step.streamWpm ?? DEFAULT_WPM;
      const msPerWord = 60_000 / wpm / factor;
      timer = setTimeout(
        () => dispatch({ type: 'WORD_TICK', count: WORDS_PER_TICK, now: formatNow() }),
        msPerWord * WORDS_PER_TICK
      );
    } else {
      // Note: changing speed mid-step restarts the remaining wait at the new
      // scale — a tiny visible artifact, deliberately not compensated for.
      timer = setTimeout(
        () => dispatch({ type: 'STEP_DONE', now: formatNow() }),
        current.durationMs / factor
      );
    }
    return () => clearTimeout(timer);
  }, [state]);

  const play = useCallback((conversation: Conversation) => {
    dispatch({
      type: 'PLAY',
      conversation,
      key: crypto.randomUUID(),
      durations: conversation.steps.map((step, i) => resolveDuration(step, i, randomRef.current)),
    });
  }, []);

  const select = useCallback((key: string) => dispatch({ type: 'SELECT', key }), []);
  const newChat = useCallback(() => dispatch({ type: 'NEW_CHAT' }), []);
  const skip = useCallback(() => dispatch({ type: 'SKIP', now: formatNow() }), []);
  const clear = useCallback(() => dispatch({ type: 'CLEAR' }), []);
  const setSpeed = useCallback(
    (speed: Speed) => dispatch({ type: 'SET_SPEED', speed, now: formatNow() }),
    []
  );

  return {
    convs: state.convs,
    activeConv,
    activeKey: state.activeKey,
    speed: state.speed,
    isPlaying: !!playing,
    play,
    select,
    newChat,
    skip,
    clear,
    setSpeed,
  };
}
